import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Minus, Plus, Send, Clock, Users, Copy, Check, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, getSettings, fmtDate, fmtDayNum, fmtMonth } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

// New purchase and promotional copy is localised without changing existing i18n keys.
const NEW_COPY = {
  es: {
    promo: "Código promocional",
    promoHelp: "Copia este código y utilízalo al comprar en la ticketera.",
    copy: "Copiar código",
    copied: "Copiado",
    buy: "Comprar entradas",
    note: "El descuento se aplica en la página de venta, no en KNDLA.",
    copyError: "Selecciona y copia el código mostrado.",
    groupOffer: (count, price) => `${count}+ entradas · ${price} por persona`,
    groupApplied: "Promoción grupal aplicada",
    groupHint: (count) => `El precio especial se activa al elegir ${count} entradas o más.`,
  },
  en: {
    promo: "Promotional code",
    promoHelp: "Copy this code and use it when purchasing on the ticketing website.",
    copy: "Copy code",
    copied: "Copied",
    buy: "Buy tickets",
    note: "Any discount is applied on the ticketing website, not by KNDLA.",
    copyError: "Select and copy the displayed code.",
    groupOffer: (count, price) => `${count}+ tickets · ${price} per person`,
    groupApplied: "Group discount applied",
    groupHint: (count) => `The special price applies when you select ${count} or more tickets.`,
  },
  ru: {
    promo: "Промокод",
    promoHelp: "Скопируйте код и используйте его при покупке билетов на сайте.",
    copy: "Копировать код",
    copied: "Скопировано",
    buy: "Купить билеты",
    note: "Скидка применяется на сайте продажи билетов, а не в KNDLA.",
    copyError: "Выделите и скопируйте показанный код.",
    groupOffer: (count, price) => `От ${count} билетов · ${price} за человека`,
    groupApplied: "Групповая скидка применена",
    groupHint: (count) => `Специальная цена действует при покупке от ${count} билетов.`,
  },
};

const newCopy = (locale) => NEW_COPY[(locale || "en").split("-")[0]] || NEW_COPY.en;
const priceRub = (value) => `${Number(value || 0).toLocaleString("ru-RU")} ₽`;
const hasGroupPromotion = (event) =>
  event?.purchase_method === "telegram" &&
  Number.isInteger(Number(event.promo_min_quantity)) &&
  Number(event.promo_min_quantity) >= 2 &&
  Number.isFinite(Number(event.promo_price_rub)) &&
  Number(event.promo_price_rub) > 0 &&
  Number(event.promo_price_rub) < Number(event.price);

const safeWebLink = (raw) => {
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:" && url.protocol !== "http:") return "";
    if (url.username || url.password) return "";
    return url.toString();
  } catch {
    return "";
  }
};

// Telegram Bot API deep linking: /start event_<id>_<quantity>.
// The bot verifies event availability and computes the official price in D1.
const telegramDestination = (event, qty) => {
  const id = Number(event.telegram_event_id);
  if (!Number.isSafeInteger(id) || id < 1 || event.purchase_unavailable) return "";
  const n = Math.max(1, Math.min(10, Number(qty) || 1));
  return `https://t.me/KNDLA_TKETS_bot?start=event_${id}_${n}`;
};

export default function Parties() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [handle, setHandle] = useState("KNDLA101");
  const { t, locale } = useLang();

  useEffect(() => {
    api
      .get("/events?type=party")
      .then((r) => setEvents(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    getSettings().then((s) => setHandle(s.telegram_handle));
  }, []);

  return (
    <section id="parties" data-testid="parties-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 md:px-8 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
      >
        <p className="text-xs font-bold uppercase tracking-[0.35em] text-flame">{t("parties.eyebrow")}</p>
        <h2 className="mt-3 font-syne text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
          {t("parties.title")}
        </h2>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-white/55">{t("parties.sub")}</p>
      </motion.div>

      {loading ? (
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-96 animate-pulse rounded-2xl border border-white/5 bg-ash" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <p data-testid="parties-empty" className="mt-14 rounded-2xl border border-white/10 bg-ash p-10 text-center text-white/50">
          {t("parties.empty")}
        </p>
      ) : (
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {events.map((e, i) => (
            <PartyCard key={e.id} event={e} index={i} onBuy={() => setSelected(e)} />
          ))}
        </div>
      )}

      <TicketModal event={selected} handle={handle} locale={locale} onClose={() => setSelected(null)} />
    </section>
  );
}

function PartyCard({ event: e, index, onBuy }) {
  const { t, locale } = useLang();
  const external = e.purchase_method === "external";
  const hasPromo = Boolean((e.promo_code || "").trim());
  const hasGroup = hasGroupPromotion(e);
  const externalLink = external ? safeWebLink(e.purchase_url) : "";
  const groupLabels = newCopy(locale);
  const showEntireImage = e.image_fit === "contain";
  const imagePosition = `${e.image_position_x ?? 50}% ${e.image_position_y ?? 50}%`;

  return (
    <motion.article
      data-testid={`party-card-${e.id}`}
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay: index * 0.1 }}
      className="group relative isolate flex min-h-[505px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-coal transition-colors duration-500 hover:border-flame/60"
    >
      {/* Full-card image rather than a separate narrow 224px header. */}
      {e.image_url && (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <img
            src={e.image_url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-85 transition-transform duration-700 group-hover:scale-[1.03]"
            style={{ objectPosition: imagePosition }}
          />
          {/* Contain keeps the poster readable, with the same photo covering the background. */}
          {showEntireImage && (
            <img
              src={e.image_url}
              alt=""
              className="absolute inset-0 h-full w-full object-contain opacity-90"
              style={{ objectPosition: imagePosition }}
            />
          )}
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/15" aria-hidden="true" />

      <div className="relative z-10 flex flex-1 flex-col p-6">
        <div className="w-fit rounded-lg border border-white/20 bg-black/65 px-3 py-2 text-center backdrop-blur-sm">
          <div className="font-syne text-2xl font-extrabold leading-none text-flame">{fmtDayNum(e.date)}</div>
          <div className="mt-0.5 text-[10px] font-bold tracking-[0.2em] text-white/90">{fmtMonth(e.date, locale)}</div>
        </div>

        <div className="mt-auto pt-24">
          <h3 className="font-syne text-2xl font-bold tracking-tight text-white [text-shadow:0_1px_6px_rgba(0,0,0,.6)]">{e.title}</h3>
          <div className="mt-3 space-y-1.5 text-sm text-white/85">
            {e.time && <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 shrink-0 text-flame" />{e.time}</p>}
            <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 shrink-0 text-flame" />{e.location}</p>
            {e.capacity > 0 && <p className="flex items-center gap-2"><Users className="h-3.5 w-3.5 shrink-0 text-flame" />{t("parties.spots", { n: e.capacity })}</p>}
          </div>
          {e.description && <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-white/75">{e.description}</p>}
          {hasGroup && (
            <div data-testid={`party-group-promo-${e.id}`} className="mt-4 w-fit rounded-lg border border-flame/55 bg-black/75 px-3 py-2 text-xs font-bold text-orange-200 backdrop-blur-sm">
              {groupLabels.groupOffer(Number(e.promo_min_quantity), priceRub(e.promo_price_rub))}
            </div>
          )}
          <div className="mt-5 flex items-center justify-between gap-3 border-t border-white/20 pt-5">
            <span className="font-syne text-xl font-extrabold text-white">
              {e.price > 0 ? e.currency === "₽" ? priceRub(e.price) : `${e.currency}${e.price}` : t("parties.free")}
            </span>
            {!external && e.purchase_unavailable ? (
              <button type="button" disabled className="cursor-not-allowed rounded-full border border-white/25 px-4 py-2.5 text-sm font-semibold text-white/60">
                {t("parties.unavailable")}
              </button>
            ) : external && !hasPromo && externalLink ? (
              <a
                data-testid={`buy-tickets-btn-${e.id}`}
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-flame px-4 py-2.5 text-sm font-bold text-white transition-all duration-300 hover:scale-105 hover:bg-ember"
              >
                {groupLabels.buy}
              </a>
            ) : (
              <button
                data-testid={`buy-tickets-btn-${e.id}`}
                onClick={onBuy}
                className="rounded-full bg-flame px-4 py-2.5 text-sm font-bold text-white transition-all duration-300 hover:scale-105 hover:bg-ember"
              >
                {external ? groupLabels.buy : t("parties.getTickets")}
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function TicketModal({ event, handle, locale, onClose }) {
  const [qty, setQty] = useState(1);
  const [copied, setCopied] = useState(false);
  const { t } = useLang();
  const labels = newCopy(locale);

  useEffect(() => {
    setQty(1);
    setCopied(false);
  }, [event]);

  if (!event) return <Dialog open={false} onOpenChange={() => {}} />;

  const external = event.purchase_method === "external";
  const promoCode = (event.promo_code || "").trim();
  const promoDescription = (event.promo_description || "").trim();
  const externalLink = external ? safeWebLink(event.purchase_url) : "";

  const hasGroup = hasGroupPromotion(event);
  const groupApplied = hasGroup && qty >= Number(event.promo_min_quantity);
  const unit = groupApplied ? Number(event.promo_price_rub) : Number(event.price);
  const total = unit * qty;
  const totalLabel =
    event.price > 0 ? event.currency === "₽" ? `${Number.isInteger(total) ? total : total.toFixed(2)} ₽` : `${event.currency}${Number.isInteger(total) ? total : total.toFixed(2)}` : t("parties.free");
  const telegramLink = telegramDestination(event, qty);

  const copyPromo = async () => {
    try {
      await navigator.clipboard.writeText(promoCode);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (external) {
    return (
      <Dialog open={!!event} onOpenChange={(open) => !open && onClose()}>
        <DialogContent data-testid="ticket-modal" className="border-white/10 bg-ash text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-syne text-2xl font-extrabold tracking-tight">{event.title}</DialogTitle>
            <DialogDescription className="text-white/55">
              {fmtDate(event.date, locale)} · {event.time} · {event.location}
            </DialogDescription>
          </DialogHeader>
          {promoCode && (
            <div className="mt-2 rounded-xl border border-flame/30 bg-coal p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/60">{labels.promo}</p>
              <p className="mt-1 text-xs leading-relaxed text-white/50">{labels.promoHelp}</p>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <span data-testid="ticket-promo-code" className="select-all break-all font-syne text-xl font-extrabold text-flame">{promoCode}</span>
                <button
                  type="button"
                  data-testid="ticket-promo-copy"
                  onClick={copyPromo}
                  className="flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-2 text-xs font-semibold transition-colors hover:border-flame hover:text-flame"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? labels.copied : labels.copy}
                </button>
              </div>
              {promoDescription && <p className="mt-2 text-sm text-white/70">{promoDescription}</p>}
            </div>
          )}
          <a
            data-testid="external-ticket-btn"
            href={externalLink || undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!externalLink}
            onClick={(e) => { if (!externalLink) e.preventDefault(); }}
            className={`flame-glow mt-2 flex items-center justify-center gap-2 rounded-full bg-flame py-3.5 text-sm font-bold text-white ${externalLink ? "hover:bg-ember" : "cursor-not-allowed opacity-50"}`}
          >
            <ExternalLink className="h-4 w-4" /> {labels.buy}
          </a>
          <p className="text-center text-xs leading-relaxed text-white/40">{labels.note}</p>
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <Dialog open={!!event} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="ticket-modal" className="border-white/10 bg-ash text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-syne text-2xl font-extrabold tracking-tight">{event.title}</DialogTitle>
          <DialogDescription className="text-white/55">
            {fmtDate(event.date, locale)} · {event.time} · {event.location}
          </DialogDescription>
        </DialogHeader>

        {promoCode && (
          <div className="mt-2 rounded-xl border border-flame/30 bg-coal p-3.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">{labels.promo}</p>
            <p data-testid="telegram-promo-code" className="mt-1 select-all break-all font-syne text-lg font-bold text-flame">{promoCode}</p>
            {promoDescription && <p className="mt-1 text-xs text-white/60">{promoDescription}</p>}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between rounded-xl border border-white/10 bg-coal p-4">
          <span className="text-sm font-semibold text-white/70">{t("modal.howMany")}</span>
          <div className="flex items-center gap-4">
            <button
              data-testid="ticket-qty-minus"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="grid h-9 w-9 place-items-center rounded-full border border-white/20 transition-colors hover:border-flame hover:text-flame"
              aria-label={t("modal.less")}
            >
              <Minus className="h-4 w-4" />
            </button>
            <span data-testid="ticket-qty-value" className="w-6 text-center font-syne text-xl font-extrabold">
              {qty}
            </span>
            <button
              data-testid="ticket-qty-plus"
              onClick={() => setQty((q) => Math.min(10, q + 1))}
              className="grid h-9 w-9 place-items-center rounded-full border border-white/20 transition-colors hover:border-flame hover:text-flame"
              aria-label={t("modal.more")}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        {hasGroup && (
          <div data-testid="ticket-group-promo" className="rounded-xl border border-flame/35 bg-flame/10 p-3 text-sm">
            <p className="font-bold text-orange-200">{labels.groupOffer(Number(event.promo_min_quantity), priceRub(event.promo_price_rub))}</p>
            <p className="mt-1 text-xs text-white/65">
              {groupApplied ? labels.groupApplied : labels.groupHint(Number(event.promo_min_quantity))}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-white/55">{t("modal.total")}</span>
          <span data-testid="ticket-total" className="font-syne text-3xl font-extrabold text-flame">
            {totalLabel}
          </span>
        </div>

        <a
          data-testid="telegram-pay-btn"
          href={telegramLink || undefined}
          onClick={(e) => { if (!telegramLink) e.preventDefault(); }}
          aria-disabled={!telegramLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`flame-glow flex items-center justify-center gap-2 rounded-full bg-flame py-3.5 text-sm font-bold text-white transition-colors ${telegramLink ? "hover:bg-ember" : "cursor-not-allowed opacity-50"}`}
        >
          <Send className="h-4 w-4" /> {t("modal.pay")}
        </a>
        <p className="text-center text-xs leading-relaxed text-white/40">{t("modal.botNote")}</p>
      </DialogContent>
    </Dialog>
  );
}
